import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { getTransactions } from '../../../auth/_requests';
import { useAuth } from '../../../auth/Auth';
import { buildingDetails } from '../../../helpers/utils/types';
import { formatCurrency } from '../../../helpers/utils/utils';
import styles from './AutopayStatusBanner.module.css';
import partyPopperIcon from '../../../assets/pages/rewards/partyPopper.svg';
import chevronRightIcon from '../../../assets/pages/rewards/chevronRight.svg';

interface props {
  buildings: buildingDetails[];
}

interface LatestAutopayTransaction {
  tenantBuildingId: string;
  status: string;
  amountPaid: number;
  currency: string;
  buildingName: string;
  timestampAdded: string;
  formattedErrorMessage?: string | null;
}

const LOOKBACK_DAYS = 7;
const FALLBACK_FAILURE_REASON = 'Your bank or card could not process this payment.';

const AutopayStatusBanner = ({ buildings }: props) => {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const [transaction, setTransaction] = useState<LatestAutopayTransaction | null>(null);
  const hasAutopay = buildings.some((item) => Boolean(item.building.hasTenantMitSubscription));

  useEffect(() => {
    if (!auth?.data.token || !hasAutopay) return;
    let cancelled = false;
    const fetchLatestTransaction = async (): Promise<void> => {
      try {
        const response = await getTransactions(auth.data.token, {
          page: 1,
          limit: 1,
          statuses: ['success', 'failed'],
          startDate: dayjs().subtract(LOOKBACK_DAYS, 'day').format('YYYY-MM-DD'),
          isRefunded: false,
          transactionType: 'debit',
        });
        const latest = response.data?.[0];
        const isFinalStatus = latest?.status === 'success' || latest?.status === 'failed';
        if (!cancelled) setTransaction(isFinalStatus ? latest : null);
      } catch {
        if (!cancelled) setTransaction(null);
      }
    };
    void fetchLatestTransaction();
    return () => {
      cancelled = true;
    };
  }, [auth?.data.token, hasAutopay]);

  const promoBanner = (
    <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 via-orange-50/70 to-white px-4 py-3">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-amber-200 bg-amber-100 text-orange-600">
        <img src={partyPopperIcon} alt="" className="h-7 w-7" aria-hidden="true" />
      </div>

      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-orange-700">
          Thanksgiving special
        </p>
        <p className="text-sm font-semibold leading-5 text-slate-900">
          We’ve doubled your referral rewards until Nov 10.
        </p>
      </div>

      <button
        type="button"
        aria-label="View referral rewards"
        onClick={() => navigate('/rewards/referrals')}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-orange-600 transition-colors hover:bg-amber-100"
      >
        <img src={chevronRightIcon} alt="" className="h-5 w-5" aria-hidden="true" />
      </button>
    </div>
  );

  if (!transaction) return promoBanner;

  const isSuccess = transaction.status === 'success';
  const amount = formatCurrency(transaction.amountPaid);
  const date = dayjs(transaction.timestampAdded).format('MMM D, YYYY');

  const handleRetry = () =>
    navigate('/pay-now', {
      state: { buildingId: transaction.tenantBuildingId, initialStep: 1, payPlan: 'direct' },
    });

  return (
    <div className="flex flex-col">
      {promoBanner}

      <div className={`${styles.banner} ${isSuccess ? styles.success : styles.failed}`} role='status'>
        <div className={styles.icon} aria-hidden='true'>
          {isSuccess ? <CheckCircle2 size={18} /> : <AlertTriangle size={18} />}
        </div>
        <div className={styles.content}>
          <div className={styles.titleRow}>
            <div className={styles.title}>
              {isSuccess
                ? `Your autopay payment of ${amount} is already processing`
                : `Your autopay payment of ${amount} failed`}
            </div>
            <span className={styles.badge}>{isSuccess ? 'Processing' : 'Action needed'}</span>
          </div>
          {isSuccess ? (
            <div className={styles.text}>
              Your {transaction.buildingName} rent was paid on {date}. No need to pay again. A manual
              payment would be a duplicate, and you don&apos;t need to cancel autopay.
            </div>
          ) : (
            <ul className={`${styles.text} ${styles.details}`}>
              <li>
                <strong>What happened:</strong> your {date} autopay payment for{' '}
                {transaction.buildingName} didn&apos;t go through.
              </li>
              <li>
                <strong>Why:</strong> {transaction.formattedErrorMessage || FALLBACK_FAILURE_REASON}
              </li>
              <li>
                <strong>How to fix it:</strong> check your payment method has enough funds, then retry.
                Autopay stays on for next month.
              </li>
            </ul>
          )}
        </div>
        {!isSuccess && (
          <button type='button' className='btn-blue' onClick={handleRetry}>
            Retry payment now
          </button>
        )}
      </div>
    </div>
  );
};

export default AutopayStatusBanner;
